/* eslint-disable better-mutation/no-mutation */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import cookieUtil from 'tradera/utils/cookie';
import {
    REGION_PICKER_DISMISSED,
    SHIPPING_COUNTRY,
} from 'tradera/constants/cookies';

const yearInMilliseconds = 1000 * 60 * 60 * 24 * 365;

type ShippingCountry = {
    countryCodeIso2: string;
};

type ShippingRegionState = {
    shippingCountry: ShippingCountry;
    shippingCountryCookieValue: string;
    availableShippingCountries: unknown[];
    availableShippingCountryCodes: string[];
    hasLoadedAvailableShippingCountries: boolean;
    isLoadingAvailableShippingCountries: boolean;
    regionPickerDismissed: boolean;
};

const shippingRegionSlice = createSlice({
    name: 'shippingRegion',
    initialState: {} as ShippingRegionState,
    reducers: {
        initialize: (_state, { payload }) => {
            return { ...payload };
        },
        setShippingCountry: {
            reducer: (state, action: PayloadAction<ShippingCountry>) => {
                const { countryCodeIso2 } = action.payload;
                state.shippingCountry = {
                    countryCodeIso2: toUpperCase(countryCodeIso2),
                };
                state.shippingCountryCookieValue = countryCodeIso2;
            },
            prepare: (payload) => {
                const { countryCodeIso2 } = payload;
                const expireDate = new Date(
                    new Date().getTime() + yearInMilliseconds,
                );
                cookieUtil.createCookie(
                    SHIPPING_COUNTRY,
                    countryCodeIso2,
                    expireDate,
                );
                return { payload };
            },
        },
        setDefaultShippingCountry: (state) => {
            state.shippingCountry = { countryCodeIso2: 'SE' };
        },
        setAvailableShippingCountries: (
            state,
            action: PayloadAction<{ shippingCountries: ShippingCountry[] }>,
        ) => {
            state.availableShippingCountries = action.payload.shippingCountries;
            state.availableShippingCountryCodes =
                action.payload.shippingCountries.map(
                    (shippingCountry) => shippingCountry.countryCodeIso2,
                );
            state.hasLoadedAvailableShippingCountries = true;
            state.isLoadingAvailableShippingCountries = false;
        },
        beginLoadingAvailableShippingOptions: (state) => {
            state.isLoadingAvailableShippingCountries = true;
        },
        dismissLocalizationRegionPicker: {
            reducer: (state) => {
                state.regionPickerDismissed = true;
            },
            prepare: (payload) => {
                cookieUtil.createCookie(REGION_PICKER_DISMISSED, true);
                return { payload };
            },
        },
    },
});

const toUpperCase = (countryCodeIso2 = '') => countryCodeIso2.toUpperCase();

export const {
    initialize,
    setShippingCountry,
    setAvailableShippingCountries,
    beginLoadingAvailableShippingOptions,
    dismissLocalizationRegionPicker,
    setDefaultShippingCountry,
} = shippingRegionSlice.actions;
export default shippingRegionSlice.reducer;
